/**
 * TestimonialsSection.tsx
 * ────────────────────────
 * Layout maquette :
 * - Gauche : grande carte avec photo + overlay nom/rôle + citation à droite
 * - Droite : encart promo violet
 * - Dots de navigation sous la carte
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const TESTIMONIALS = [
  {
    name:     'Cécilia Icard',
    id:       'cecilia', // textes : testimonials.items.cecilia.quote / .role
    photo:    '/public/cecilia_oostaoo_rh.png',
    initials: 'CB',
  },
  {
    name:     'Thomas D.',
    id:       'thomas_d', // textes : testimonials.items.thomas_d.quote / .role
    photo:    'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=440&q=80',
    initials: 'TD',
  },
  {
    name:     'Maître Sophie R.',
    id:       'sophie', // textes : testimonials.items.sophie.quote / .role
    photo:    'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=440&q=80',
    initials: 'SR',
  },
  {
    name:     'Thomas L.',
    id:       'thomas_l', // textes : testimonials.items.thomas_l.quote / .role
    photo:    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=440&q=80',
    initials: 'TL',
  },
];

const DELAY = 8000;

export const TestimonialsSection = () => {
  const { t: tr } = useTranslation();
  const [active, setActive] = useState(0);
  const [tick,   setTick]   = useState(0); // reset le timer au clic
  const t = TESTIMONIALS[active];

  const goTo = useCallback((i: number) => {
    setActive(i);
    setTick(t => t + 1); // force re-création de l'intervalle
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setActive(i => (i + 1) % TESTIMONIALS.length);
    }, DELAY);
    return () => clearInterval(id);
  }, [tick]); // repart à zéro à chaque clic manuel

  return (
    <section className="py-24 bg-muted/30 border-t border-border">
      <div className="max-w-[1100px] mx-auto px-6 sm:px-8">

        {/* Header */}
        <div className="mb-10">
          <span className="block text-[10px] font-bold uppercase tracking-[.16em] text-primary mb-3">
            {tr('testimonials.eyebrow')}
          </span>
          <h2 className="font-serif text-[30px] sm:text-[34px] font-bold text-foreground tracking-[-0.03em] leading-tight">
            {tr('testimonials.title_start')}<br />
            <em className="not-italic text-primary">{tr('testimonials.title_highlight')}</em>
          </h2>
        </div>

        {/* Layout principal */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-stretch">

          {/* ── Carte témoignage ── */}
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr] sm:h-[360px]">

              {/* Photo */}
              <div className="relative overflow-hidden h-[260px] sm:h-full">
                <img
                  src={t.photo}
                  alt={t.name}
                  className="w-full h-full object-cover object-top"
                />
                {/* Overlay gradient + identité */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-5 pb-4 pt-10">
                  <p className="font-serif text-[14px] font-semibold text-white">{t.name}</p>
                  <p className="text-[11px] text-white/50 mt-0.5">{tr(`testimonials.items.${t.id}.role`)}</p>
                </div>
              </div>

              {/* Citation */}
              <div className="px-8 py-8 sm:py-10 flex flex-col justify-center">
                {/* Guillemets */}
                <div
                  className="text-[72px] leading-none font-serif select-none -mt-2 -ml-1 mb-1"
                  style={{ color: 'hsl(var(--primary) / 0.10)' }}
                  aria-hidden="true"
                >
                  "
                </div>
                <p
                  className="font-serif text-[17px] sm:text-[19px] font-light italic text-foreground leading-[1.75]"
                  dangerouslySetInnerHTML={{ __html: tr(`testimonials.items.${t.id}.quote`) }}
                />
              </div>
            </div>
          </div>

          {/* ── Encart promo violet ── */}
          <div className="rounded-2xl border border-violet-200 dark:border-violet-800/60 bg-violet-50 dark:bg-violet-950/20 p-7 flex flex-col justify-between h-full">
            <div>
              {/* Icône éclair */}
              <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700 flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <p className="text-[9px] font-bold uppercase tracking-[.12em] text-violet-600 dark:text-violet-400 mb-1.5">
                {tr('testimonials.promo_eyebrow')}
              </p>
              <h3 className="font-serif text-[20px] font-bold text-foreground tracking-[-0.02em] leading-[1.2] mb-3">
                {tr('testimonials.promo_title_1')}<br />
                {tr('testimonials.promo_title_2')}
              </h3>
              <p className="text-[12px] text-muted-foreground leading-relaxed mb-6">
                {tr('testimonials.promo_desc')}
              </p>
            </div>
            <div>
              <Link
                to="/pricing"
                className="flex items-center justify-center w-full px-4 py-3.5 rounded-xl bg-violet-600 text-white text-[15px] font-bold hover:bg-violet-700 transition-colors shadow-md shadow-violet-600/20"
              >
                {tr('common.subscribe')}
              </Link>
              <p className="text-center text-[11px] text-muted-foreground mt-2.5">
                {tr('testimonials.promo_price')}
              </p>
            </div>
          </div>
        </div>

        {/* Dots navigation */}
        <div className="flex justify-start items-center gap-2 mt-5">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={tr('testimonials.dot_label', { n: i + 1 })}
              className="transition-all duration-300 rounded-full"
              style={{
                width:      i === active ? '20px' : '6px',
                height:     '6px',
                borderRadius: i === active ? '3px' : '50%',
                background: i === active ? 'hsl(var(--primary))' : 'hsl(var(--border))',
              }}
            />
          ))}
        </div>

      </div>
    </section>
  );
};